import { React, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PanelCard from '../panel-card/panel-card';
import Tabs from '../tabs/custom-tab';
import IconButton from '../icon-button/icon-button';
import UserImage from '../user-image/user-image';
import ListingStatus from '../listing-status/listing-status';
import Skeleton from '../skeleton/skeleton';

import { BoxOutlineIcon } from '../icons/icons';
import {
  DiscountIcon,
  DuedateChangeIcon,
  PricePerUnitChange,
  QuantityChangeIcon,
} from '../icons/orderhistory-icons';

import './order-history.scss';

export default function OrderHistory({
  stagesData,
  itemInfo,
  lineItemHistoryLoading,
}) {
  const [t] = useTranslation('manageorders');
  const formatDate = (date) => new Date(date).toLocaleDateString('en-GB');

  const skeletonData = useMemo(() => {
    return Array.from({ length: stagesData?.length }).map((_, stageIdx) => ({
      stageKey: `skeleton-stage-${stageIdx}`,
      events: Array.from({ length: 2 }).map(
        (__, eventIdx) => `skeleton-event-${stageIdx}-${eventIdx}`
      ),
    }));
  }, []);

  return (
    <PanelCard cardTitle="Order History" noSpacing>
      <div className="order-history-tabs-header">
        <Tabs
          disableURL
          className="history-tab large"
          bodyClass="position-relative"
        >
          {/* ================= ORDER UPDATES ================= */}
          <div label="Order Updates" value="Order Updates">
            {lineItemHistoryLoading && (
              <>
                <div className="order-header-info side-info">
                  <p className="part">
                    <Skeleton skHeight="16px" skWidth="140px" />
                  </p>
                  <p className="total-qty">
                    <Skeleton skHeight="16px" skWidth="110px" />
                  </p>
                </div>

                <div className="stage-timeline">
                  {skeletonData.map((stage) => (
                    <div className="stage" key={stage.stageKey}>
                      <div className="stage-left">
                        <Skeleton
                          skHeight="24px"
                          skWidth="24px"
                          skRadius="50%"
                        />
                        <div className="stage-line" />
                      </div>

                      <div className="stage-content">
                        <h3 className="stage-title">
                          <Skeleton skHeight="18px" skWidth="35%" />
                        </h3>

                        {stage.events.map((eventKey) => (
                          <div className="stage-item" key={eventKey}>
                            <div className="d-flex align-items-center">
                              <Skeleton skHeight="14px" skWidth="70px" />
                              <Skeleton
                                skHeight="14px"
                                skWidth="80px"
                                skMargin="0 0 0 8px"
                                skRadius="12px"
                              />
                            </div>
                            <p className="update-info">
                              <Skeleton skHeight="12px" skWidth="55%" />
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {!lineItemHistoryLoading && stagesData?.length && (
              <>
                <div className="order-header-info side-info">
                  <p className="part">
                    Part <span># {itemInfo?.lineItemName}</span>
                  </p>
                  <p className="total-qty">
                    Total Qty {itemInfo?.totalQuantity}
                  </p>
                </div>

                <div className="stage-timeline">
                  {stagesData.map((stage, stageIndex) => (
                    <div className="stage" key={stage.stageKey || stageIndex}>
                      <div className="stage-left">
                        <IconButton
                          type="primary-gradient"
                          className="stage-icon"
                        >
                          <BoxOutlineIcon height="1.25vw" width="1.25vw" />
                        </IconButton>
                        <div className="stage-line" />
                      </div>

                      <div className="stage-content">
                        <h3 className="stage-title">
                          {stage.stageNameTranslation || stage.stageName}
                        </h3>

                        {stage.events.length === 0 ? (
                          <div className="stage-item">
                            <p className="update-info">No updates available</p>
                          </div>
                        ) : (
                          stage.events.map((event) => (
                            <div className="stage-item">
                              <div>
                                <span className="qty">
                                  {event.quantity} {event.unit}
                                </span>
                                <ListingStatus
                                  statusLabel={event.status}
                                  className="status"
                                />
                              </div>

                              <p className="update-info">
                                Updated on {formatDate(event.updatedOn)}, By
                                <span className="d-flex align-center">
                                  <span className="users-name text-primary">
                                    {event.updatedBy}
                                  </span>
                                  {/*    <UserImage removeTooltip size="xx-small" /> */}
                                </span>
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ================= PO CHANGES ================= */}
          {false && (
            <div label="PO Changes" value="PO Changes">
              <div className="order-header-info side-info">
                <p className="part">
                  Part <span># C823451</span>
                </p>
                <p className="total-qty">Total Qty 10,000 PC</p>
              </div>
              <div className="po-changes-container">
                <p className="timeline-date">13th Mar 2025</p>

                <div className="po-change-item">
                  <div className="timeline-col">
                    <IconButton
                      type="primary-gradient"
                      className="timeline-icon"
                    >
                      <PricePerUnitChange />
                    </IconButton>
                  </div>

                  <UserImage removeTooltip size="medium" type="square" />

                  <div className="change-content">
                    <p className="title">Price per unit change</p>
                    <p className="value">1$ to 1.5$</p>
                    <p className="byuser">
                      {t('manageordersscreen.order.by')} Nick Johnson,
                      <span className="time">
                        {t('manageordersscreen.order.at')} 09:30 AM
                      </span>
                    </p>
                  </div>

                  <ListingStatus statusLabel="Rejected" />
                </div>

                <div className="po-change-item">
                  <div className="timeline-col">
                    <IconButton
                      type="primary-gradient"
                      className="timeline-icon"
                    >
                      <DiscountIcon />
                    </IconButton>
                  </div>

                  <UserImage removeTooltip size="medium" type="square" />

                  <div className="change-content">
                    <p className="title">Discount</p>
                    <p className="value">10% to 15%</p>
                    <p className="byuser">
                      {t('manageordersscreen.order.by')} Nick Johnson,
                      <span className="time">
                        {t('manageordersscreen.order.at')} 09:30 AM
                      </span>
                    </p>
                  </div>

                  <ListingStatus statusLabel="Accepted" />
                </div>

                <div className="po-change-item">
                  <div className="timeline-col">
                    <IconButton
                      type="primary-gradient"
                      className="timeline-icon"
                    >
                      <QuantityChangeIcon />
                    </IconButton>
                  </div>

                  <UserImage removeTooltip size="medium" type="square" />

                  <div className="change-content">
                    <p className="title">Quantity Change</p>
                    <p className="value">100 to 200</p>
                    <p className="byuser">
                      {t('manageordersscreen.order.by')} Nick Johnson,
                      <span className="time">
                        {t('manageordersscreen.order.at')} 09:30 AM
                      </span>
                    </p>
                  </div>

                  <ListingStatus statusLabel="Requested" />
                </div>

                <div className="po-change-item">
                  <div className="timeline-col">
                    <IconButton
                      type="primary-gradient"
                      className="timeline-icon"
                    >
                      <DuedateChangeIcon />
                    </IconButton>
                  </div>

                  <UserImage removeTooltip size="medium" type="square" />

                  <div className="change-content">
                    <p className="title">Due date change</p>
                    <p className="value">15/05/2023 to 17/11/2023</p>
                    <p className="byuser">
                      {t('manageordersscreen.order.by')} Nick Johnson,
                      <span className="time">
                        {t('manageordersscreen.order.at')} 09:30 AM
                      </span>
                    </p>
                  </div>

                  <ListingStatus statusLabel="Pending" />
                </div>
              </div>
            </div>
          )}
        </Tabs>
      </div>
    </PanelCard>
  );
}
OrderHistory.propTypes = {
  stagesData: PropTypes.instanceOf(Array),
  itemInfo: PropTypes.instanceOf(Object),
  lineItemHistoryLoading: PropTypes.bool,
};
OrderHistory.defaultProps = {
  stagesData: [],
  itemInfo: {},
  lineItemHistoryLoading: false,
};
